import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Store, Calendar, Utensils, Quote, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipos de parceiros estáticos (raramente mudam)
const tiposParceiros = [
  {
    id: 1,
    titulo: 'Restaurantes & Bares',
    descricao: 'Fornecimento regular de cortes premium para estabelecimentos que levam churrasco a sério.',
    icone: 'restaurant'
  },
  {
    id: 2,
    titulo: 'Eventos & Catering',
    descricao: 'Soluções personalizadas para eventos corporativos, casamentos e celebrações especiais.',
    icone: 'calendar'
  },
  {
    id: 3,
    titulo: 'Mercados & Lojas Gourmet',
    descricao: 'Parcerias para revenda de produtos defumados premium em estabelecimentos de qualidade.',
    icone: 'store'
  }
];

const Parceiros = () => {
  const [parceiros, setParceiros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchParceiros = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/parceiros`);
        if (response.ok) {
          const data = await response.json();
          setParceiros(data);
        }
      } catch (err) {
        console.error('Erro ao carregar parceiros:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchParceiros();
  }, []);

  const getIcon = (icone) => {
    switch (icone) {
      case 'restaurant':
        return <Utensils className="w-10 h-10 text-[#B8860B]" />;
      case 'calendar':
        return <Calendar className="w-10 h-10 text-[#B8860B]" />;
      case 'store':
        return <Store className="w-10 h-10 text-[#B8860B]" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] pt-20">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1708388064322-c15cca1f86a7?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#1a1a1a]/80 via-[#1a1a1a]/60 to-[#1a1a1a]" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 z-10 text-center">
          <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-4">Colaboração</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#E8DDCB] mb-6" style={{fontFamily: "'Playfair Display', serif"}}>
            Parceiros
          </h1>
          <div className="w-16 h-[2px] bg-[#B8860B] mx-auto" />
        </div>
      </section>

      {/* Tipos de Parceiros */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-4">Categorias</p>
            <h2 className="text-4xl font-semibold text-[#E8DDCB]" style={{fontFamily: "'Playfair Display', serif"}}>
              Tipos de Parceiros
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiposParceiros.map((tipo) => (
              <Card key={tipo.id} className="bg-[#242424] border border-[#B8860B]/10 hover:border-[#B8860B]/40 transition-all">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="flex justify-center">
                    {getIcon(tipo.icone)}
                  </div>
                  <h3 className="text-xl font-semibold text-[#E8DDCB]" style={{fontFamily: "'Playfair Display', serif"}}>
                    {tipo.titulo}
                  </h3>
                  <p className="text-[#E8DDCB]/60 text-sm leading-relaxed">
                    {tipo.descricao}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Logos dos Parceiros */}
      <section className="py-20 bg-[#242424]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-4">Confiança</p>
            <h2 className="text-4xl font-semibold text-[#E8DDCB]" style={{fontFamily: "'Playfair Display', serif"}}>
              Quem Confia em Nós
            </h2>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 text-[#B8860B] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {parceiros.map((parceiro) => (
                <div 
                  key={parceiro.id}
                  className="bg-[#1a1a1a] rounded-lg p-8 flex items-center justify-center border border-[#B8860B]/10 hover:border-[#B8860B]/40 transition-all"
                >
                  <img 
                    src={parceiro.logo} 
                    alt={parceiro.nome}
                    className="max-w-full h-auto opacity-80 hover:opacity-100 transition-opacity"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Depoimentos */}
      <section className="py-20 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#B8860B] text-sm tracking-[0.3em] uppercase mb-4">Testemunhos</p>
            <h2 className="text-4xl font-semibold text-[#E8DDCB]" style={{fontFamily: "'Playfair Display', serif"}}>
              O Que Dizem
            </h2>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 text-[#B8860B] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {parceiros.map((parceiro) => (
                <Card key={parceiro.id} className="bg-[#242424] border border-[#B8860B]/10">
                  <CardContent className="p-8">
                    <Quote className="w-8 h-8 text-[#B8860B]/50 mb-4" />
                    <p className="text-[#E8DDCB]/80 text-lg mb-6 italic leading-relaxed">
                      "{parceiro.depoimento}"
                    </p>
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-[#B8860B]/20 flex items-center justify-center">
                        <span className="text-[#B8860B] font-semibold">
                          {parceiro.responsavel.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-[#E8DDCB] font-medium">{parceiro.responsavel}</p>
                        <p className="text-[#B8860B] text-sm">{parceiro.nome}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#242424]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-semibold text-[#E8DDCB]" style={{fontFamily: "'Playfair Display', serif"}}>
              Quer ser Parceiro?
            </h2>
            <p className="text-[#E8DDCB]/70 text-lg">
              Junte-se à nossa rede e ofereça BBQ premium aos seus clientes.
            </p>
            <Button 
              asChild
              size="lg"
              className="bg-[#B8860B] hover:bg-[#D4A84B] text-[#1a1a1a] font-semibold tracking-wider uppercase text-sm px-10 py-6"
            >
              <Link to="/contacto">Contactar</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Parceiros;
